import {
  IQueryParam,
  IRendimientoCombustibleVehiculo,
  ModoConsumoVehiculo,
} from 'modelos/src';

/**
 * Consumo promedio medido por el sensor de combustible: lo calcula
 * gestion-cron cada hora sobre los últimos 30 días (resumen 'Rendimiento
 * Combustible Vehículos', un documento por vehículo). Acá solo se pide y se
 * formatea, igual para el panel del mapa y el detalle del vehículo.
 */

export const TIPO_RESUMEN_RENDIMIENTO = 'Rendimiento Combustible Vehículos';

export function queryRendimientoCombustible(idActivo: string): IQueryParam {
  return {
    filter: JSON.stringify({
      tipo: TIPO_RESUMEN_RENDIMIENTO,
      idsAsignados: idActivo,
    }),
    select: 'resumen periodoInicio periodoFin',
    limit: 1,
    includeChildren: true,
    excludeTotalCount: true,
  };
}

export interface VistaConsumoRango extends VistaRendimientoCombustible {
  /** Litros consumidos en el viaje o período (ej. "13 L") */
  total?: string;
  /** Datos sin formatear (para el Excel) */
  datos: IRendimientoCombustibleVehiculo;
  modo: ModoConsumoVehiculo;
}

/**
 * Consumo de un viaje o período (GET /activos/:id/consumo-combustible). El
 * total se muestra aunque no alcance para un promedio confiable.
 */
export function vistaConsumoRango(
  r: IRendimientoCombustibleVehiculo | null | undefined,
  modo: ModoConsumoVehiculo = 'km',
): VistaConsumoRango | null {
  if (!r) return null;
  const vista: VistaConsumoRango = {
    ...vistaRendimientoCombustible(r, modo)!,
    datos: r,
    modo,
  };
  if (r.litrosConsumidos != null && r.litrosConsumidos > 0) {
    vista.total = `${num(r.litrosConsumidos)} L`;
  }
  // El desglose de la ventana de 30 días no aplica: se arma el del rango
  const partes: string[] = [];
  if (r.nivelInicial != null && r.nivelFinal != null) {
    partes.push(`Nivel ${num(r.nivelInicial, 0)} → ${num(r.nivelFinal, 0)} L`);
  }
  if (r.kmRecorridos != null) partes.push(`${num(r.kmRecorridos)} km`);
  if (r.horasMotor != null) partes.push(`${num(r.horasMotor)} h de motor`);
  if (r.cantidadCargas) {
    partes.push(
      `${r.cantidadCargas} carga${r.cantidadCargas > 1 ? 's' : ''} (${num(r.litrosCargados ?? 0, 0)} L)`,
    );
  }
  if (r.cantidadDescargas) {
    partes.push(
      `${r.cantidadDescargas} descarga${r.cantidadDescargas > 1 ? 's' : ''} (${num(r.litrosDescargados ?? 0, 0)} L, no cuentan como consumo)`,
    );
  }
  vista.detalle = partes.join(' · ');
  return vista;
}

export interface VistaRendimientoCombustible {
  /** Valor principal según el modo del vehículo (ej. "39,6 L/100km") */
  principal?: string;
  /** Equivalente (ej. "2,53 km/L"), solo en modo km */
  secundario?: string;
  /** Motivo cuando no hay datos suficientes para el modo del vehículo */
  sinDatos?: string;
  /** Desglose para el tooltip */
  detalle: string;
}

const num = (n: number, dec = 1) =>
  n.toLocaleString('es-AR', { maximumFractionDigits: dec });

export function vistaRendimientoCombustible(
  r: IRendimientoCombustibleVehiculo | undefined,
  modo: ModoConsumoVehiculo = 'km',
): VistaRendimientoCombustible | null {
  if (!r) return null;

  const partes: string[] = [];
  const dias = r.diasVentana ?? 30;
  if (r.litrosConsumidos != null) {
    const base =
      modo === 'hora' && r.horasMotor != null
        ? `${num(r.horasMotor)} h de motor`
        : r.kmRecorridos != null
          ? `${num(r.kmRecorridos, 0)} km`
          : undefined;
    partes.push(
      `Últimos ${dias} días: ${num(r.litrosConsumidos, 0)} L consumidos` +
        (base ? ` en ${base}` : ''),
    );
  }
  if (r.cantidadCargas) {
    partes.push(
      `${r.cantidadCargas} carga${r.cantidadCargas > 1 ? 's' : ''} (${num(r.litrosCargados ?? 0, 0)} L)`,
    );
  }
  if (r.cantidadDescargas) {
    partes.push(
      `${r.cantidadDescargas} descarga${r.cantidadDescargas > 1 ? 's' : ''} (${num(r.litrosDescargados ?? 0, 0)} L, no cuentan como consumo)`,
    );
  }
  if (r.cantidadFalsasAlarmas) {
    partes.push(
      `${r.cantidadFalsasAlarmas} falsa${r.cantidadFalsasAlarmas > 1 ? 's' : ''} alarma${r.cantidadFalsasAlarmas > 1 ? 's' : ''} de descarga descartada${r.cantidadFalsasAlarmas > 1 ? 's' : ''}`,
    );
  }

  const vista: VistaRendimientoCombustible = { detalle: partes.join(' · ') };
  if (modo === 'hora') {
    if (r.rendimientoLh != null)
      vista.principal = `${num(r.rendimientoLh)} L/h`;
  } else if (r.rendimientoL100km != null) {
    vista.principal = `${num(r.rendimientoL100km)} L/100km`;
    if (r.rendimientoKmL != null)
      vista.secundario = `${num(r.rendimientoKmL, 2)} km/L`;
  }
  if (!vista.principal) {
    vista.sinDatos = r.motivoSinDatos || 'Sin datos suficientes';
  }
  return vista;
}
